import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { Search as DeepSeekIcon, Save, CheckCircle, AlertCircle } from 'lucide-react';
import { checkDeepseekConnection } from '@/lib/deepseek'; // Assuming a check function exists

const DeepSeekSettings: React.FC = () => {
  const [apiKey, setApiKey] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);

  useEffect(() => {
    const savedKey = localStorage.getItem('deepseek_api_key');
    if (savedKey) {
      setApiKey(savedKey);
      // Optionally, validate the saved key on load
      // validateKey(savedKey);
    }
  }, []);

  const validateKey = async (keyToValidate: string) => {
    if (!keyToValidate.trim()) {
      setIsValid(null);
      return;
    }
    setIsValidating(true);
    try {
      const isConnected = await checkDeepseekConnection(); // This might need adjustment
      setIsValid(isConnected);
      if (!isConnected) {
        toast({
          title: "Connection Failed",
          description: "Could not connect to DeepSeek with the provided key or general connection failed.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setIsValid(false);
      toast({
        title: "Validation Error",
        description: "Failed to validate DeepSeek API key.",
        variant: "destructive",
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast({
        title: "API Key Required",
        description: "Please enter your DeepSeek API key.",
        variant: "destructive",
      });
      return;
    }
    setIsSaving(true);
    localStorage.setItem('deepseek_api_key', apiKey);
    await validateKey(apiKey); // Validate after saving
    setIsSaving(false);
    toast({
      title: "DeepSeek API Key Saved",
      description: "Your DeepSeek API key has been saved locally.",
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <DeepSeekIcon className="h-6 w-6 text-blue-500" />
          <CardTitle>DeepSeek API Settings</CardTitle>
        </div>
        <CardDescription>
          Configure your DeepSeek API key to enable DeepSeek models.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label htmlFor="deepseekApiKey" className="text-sm font-medium mb-1 block">
            DeepSeek API Key
          </label>
          <div className="flex gap-2">
            <Input
              id="deepseekApiKey"
              type="password"
              placeholder="Enter your DeepSeek API Key"
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
                setIsValid(null);
              }}
              className="flex-1"
            />
            <Button onClick={handleSave} disabled={isSaving || isValidating}>
              <Save className="mr-2 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Key'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Your API key is stored locally in your browser.
          </p>
        </div>
        {isValidating && (
          <p className="text-sm text-muted-foreground">Validating key...</p>
        )}
        {isValid === true && (
          <div className="flex items-center text-sm text-green-600">
            <CheckCircle className="mr-2 h-4 w-4" />
            DeepSeek API connection successful.
          </div>
        )}
        {isValid === false && (
          <div className="flex items-center text-sm text-red-600">
            <AlertCircle className="mr-2 h-4 w-4" />
            Failed to connect or validate DeepSeek API key.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DeepSeekSettings;